import { GAME } from '@/config/game';
import { SPAWNS, WAVE_LAYOUT } from '@/config/waves';
import { t } from '@/i18n';
import type { Boss } from '@/bosses/boss-base';
import type { MinionKind } from '@/entities/minion-defs';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import { sfx } from '@/audio/sfx';
import { LAYOUTS } from '@/arena/layouts';
import { AdvanceDirector } from './advance-director';
import type { RunState } from './run-state';
import { difficultyFor, layoutIndexFor, waveRulesFor, type WaveRules } from './sector-hooks';
import {
  WAVE_SALT, bunkerCount, bunkerSlot, formationPoint, roomHash, segmentWaveEnds, squadKind,
  squadSize, wavesFor,
} from './wave-plan';

/**
 * Wave mode (docs/12 C8): minion waves soften the ship up while the boss
 * waits offstage; mid-fight side squads (M2) once it is out. The plan is
 * pure (wave-plan.ts) — this module spawns it and runs the clock. ADVANCE
 * rooms hand the clock to the AdvanceDirector (segments + gates).
 */
export class WaveDirector {
  readonly advance: AdvanceDirector;
  private readonly anchor = { x: 0, y: 0 };
  /** The current room's wave rules — resolved once per room (no per-frame alloc) */
  private rules: WaveRules | null = null;
  /** Side-squad kinds this room has already introduced */
  private addKinds: ReadonlyArray<MinionKind> = WAVE_LAYOUT.adds.kinds;

  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly hud: Hud,
    /** The death screen is up — a cleared wave must not advance under it */
    private readonly fightOver: () => boolean,
  ) {
    this.advance = new AdvanceDirector(world, run, hud, {
      startWave: () => this.startWave(),
      releaseBoss: () => this.releaseBoss(),
      fightOver,
    });
  }

  /**
   * Room start: the boss is HELD until the waves clear. Death retries
   * resume at the wave the player fell on.
   */
  beginRoom(retry: boolean): void {
    const run = this.run;
    const wave = run.wave;
    const rules = (this.rules = waveRulesFor(run, run.roomInSector));
    const intro = WAVE_LAYOUT.adds.kinds.filter((k) => rules.kinds.includes(k));
    this.addKinds = intro.length > 0 ? intro : WAVE_LAYOUT.adds.kinds;
    const planned = wavesFor(run.seed, run.roomInSector, rules.waves);
    wave.addTimer = GAME.run.bossAdds.intervalMin;
    run.bossHits = 0;
    const arena = this.world.arena;
    if (arena.isRoute) {
      // Segments + gates run the waves; the boss waits in its chamber
      this.advance.begin(arena, planned, retry);
      this.world.setBossVisible(!wave.bossHeld);
      return;
    }
    this.advance.reset();
    wave.total = planned;
    if (!retry) wave.index = 0;
    wave.index = Math.min(wave.index, wave.total);
    wave.bossHeld = wave.index < wave.total;
    this.world.setBossVisible(!wave.bossHeld);
    if (wave.bossHeld) this.startWave();
  }

  /** Waves the NEXT room will run — the shop preview (docs/12 B5). */
  previewWaves(): number {
    const run = this.run;
    const room = run.roomInSector + run.nextRoomStep;
    const planned = wavesFor(run.seed, room, waveRulesFor(run, room).waves);
    const layout = LAYOUTS[layoutIndexFor(run, room, run.arenaIndex + run.nextRoomStep)];
    const segments = layout?.route?.segments.length ?? 0;
    return segments > 0 ? segmentWaveEnds(planned, segments)[segments - 1]! : planned;
  }

  private startWave(): void {
    const { world, run } = this;
    const wave = run.wave;
    const room = run.roomInSector;
    const rules = this.rules ?? waveRulesFor(run, room);
    const lean = this.advance.active ? WAVE_LAYOUT.advanceSquadMul : 1;
    const count = squadSize(wave.index, difficultyFor(run).waveDensity * lean);
    const h = roomHash(run.seed, room, WAVE_SALT + wave.index);
    const pcx = world.player.x;
    const pcy = world.player.y;

    // Statics first (docs/12 M8): bunker/dome formations on preset slots
    const bunkers = bunkerCount(room, wave.index, rules.bunkerFrom);
    for (let i = 0; i < bunkers; i++) {
      const slot = bunkerSlot(h, i);
      const p = world.arena.clearPoint(
        slot[0], slot[1], pcx, pcy, SPAWNS.bunker.away, SPAWNS.bunker.clear,
      );
      world.minions.spawn('bunker', 1, p.x, p.y);
    }
    if (bunkers > 0 && room >= rules.domeFrom && wave.index === wave.total - 1) {
      const d = SPAWNS.dome;
      const p = world.arena.clearPoint(d.at[0], d.at[1], pcx, pcy, d.away, d.clear);
      world.minions.spawn('dome', 1, p.x, p.y); // the big-shell anchor (M3)
    }

    // Light squad: a formation + a kind mix per wave (docs/12 M2). Every
    // spawn clears rocks AND keeps off the player's safe zone
    for (let i = 0; i < count; i++) {
      formationPoint(h, i, count, wave.index, this.anchor);
      const p = world.arena.clearPoint(
        this.anchor.x, this.anchor.y, pcx, pcy, SPAWNS.squad.away, SPAWNS.squad.clear,
      );
      world.minions.spawn(squadKind(h, i, rules.kinds, rules.eliteRatio), 1, p.x, p.y);
    }

    wave.spawned = world.minions.count;
    wave.stallTimer = 0;
    this.hud.notify(t('hud.wave', { n: wave.index + 1, m: wave.total }), 'wave');
  }

  /** Wave cleared → next wave, or unleash the held boss. */
  private advanceWave(): void {
    const { world, run } = this;
    run.wave.index++;
    world.gems.spawnBurst(world.player.x, world.player.y, WAVE_LAYOUT.clearGems, 1, run.rng);
    if (run.wave.index >= run.wave.total) this.releaseBoss();
    else this.startWave();
  }

  /** The held boss enters (last wave down / the chamber reached). */
  private releaseBoss(): void {
    const { world, run } = this;
    run.wave.bossHeld = false;
    run.bossHits = 0; // "untouchable" counts from the boss's arrival
    world.setBossVisible(true);
    this.hud.notify(t('hud.bossIncoming'), 'boss');
    world.camera.shake(WAVE_LAYOUT.bossArrivalShake);
    sfx.bossDeath(); // deep rumble doubles as the arrival sting
  }

  /**
   * Wave-phase clock, AFTER this frame's combat. Failsafe: a wave can NEVER
   * soft-lock the run — leftovers of a stalled wave detonate (N hotfix).
   */
  tickHeld(dt: number): void {
    if (this.advance.active) {
      this.advance.tick(dt);
      return;
    }
    const { world, run } = this;
    run.wave.stallTimer += dt;
    if (run.wave.stallTimer > WAVE_LAYOUT.stallSeconds && world.minions.count > 0) {
      const blast = world.minions.nearest(world.player.x, world.player.y);
      world.minions.clear();
      if (blast) world.fx.bigExplosion(blast.x, blast.y);
      sfx.explosion(WAVE_LAYOUT.stallBlastSfx);
    }
    if (world.minions.count === 0 && !this.fightOver()) this.advanceWave();
  }

  /** Side squads drop in mid-fight (docs/12 M2) — small, light, spicy. */
  tickAdds(dt: number, boss: Boss): void {
    const { world, run } = this;
    if (this.advance.active) this.advance.tickBoss(dt);
    const adds = GAME.run.bossAdds;
    const addsFrom = this.rules?.addsFrom ?? adds.minRoom;
    if (run.roomInSector < addsFrom || !boss.isVulnerable) return;
    run.wave.addTimer -= dt;
    if (run.wave.addTimer > 0) return;
    run.wave.addTimer = run.rng.range(adds.intervalMin, adds.intervalMax);
    if (world.minions.count > adds.maxAlive) return;
    const A = WAVE_LAYOUT.adds;
    // Side squads only field kinds the room has already introduced
    const kinds = this.addKinds;
    const kind = kinds[run.rng.int(0, kinds.length - 1)]!;
    const edge = world.arena.clearPoint(
      run.rng.chance(A.leftChance) ? A.edgeX[0] : A.edgeX[1], run.rng.range(A.edgeY[0], A.edgeY[1]),
      world.player.x, world.player.y, SPAWNS.adds.away, SPAWNS.adds.clear,
    );
    world.minions.spawn(kind, run.rng.int(A.size[0], A.size[1]), edge.x, edge.y);
  }
}
